import * as wasm from '@soniq/util-soniqstudio-wasm'
import type {
  CreatedFields,
  DeletedFields,
  OmitCreated,
  OmitDeleted,
  ProfileFields,
  SchemaWithId,
} from '@soniq/schema'
import { ISODateTimeString } from '@pogokid/util/datetime'
import {
  Kysely,
  SelectQueryBuilder,
} from '@pogokid/dblite/kysely'
import {
  CalendarSchema,
  ScheduleInstanceSchema,
  ScheduleSchema,
} from '@soniq/schema-scheduling'
import {
  ContactRelationshipSchema,
  ContactSchema,
  InviteSchema,
  LocationSchema,
  ProfileSchema,
  ProfileSettingsSchema,
} from '@soniq/schema-connection'
import {
  CreditNoteLineSchema,
  CreditNoteSchema,
  InvoiceActivitySchema,
  InvoiceLineSchema,
  InvoiceSchema,
  PaymentProviderAccountSchema,
  PaymentSchema,
  PriceSchema,
  ProfileSubscriptionSchema,
  SubscriptionFeatureSchema,
  SubscriptionPlanFeatureSchema,
  SubscriptionPlanSchema,
  WalletEntrySchema,
} from '@soniq/schema-payments'
import { MessageSchema } from '@soniq/schema-messaging'

export interface EventSourcingDatabase {
  domain_event: TDomainEvent<string, string>
  domain_event_refs: DomainEventRef
  projection_state: ProjectionStateRecord
}

export type DomainEventRefSyncState =
  | 'idle'
  | 'syncing'
  | 'live'
  | 'offline'
  | 'error'

/**
 * All Sync States in order of importance
 */
export const DOMAIN_EVENT_REF_ALL_SYNC_STATES: DomainEventRefSyncState[] = [
  'error',
  'offline',
  'syncing',
  'live',
  'idle'
]

export interface DomainEventRef {
  aggregate_type: string
  profile_id: string
  is_active: boolean
  sync_state: DomainEventRefSyncState
  message: string | null
  last_pull: ISODateTimeString | null
  last_push: ISODateTimeString | null
  last_seen_sequence: number | null
}

export interface ProjectionStateRecord {
  id?: number
  aggregate_type: string
  profile_id: string
  projection_state: DomainEventRefSyncState
  message: string | null
  last_processed_sequence: number
  last_updated_at: ISODateTimeString | null
}

export interface EventMetadata {
  [key: string]: unknown
}

export interface TDomainEvent<T = unknown, M = EventMetadata>
  extends Omit<
    wasm.DomainEvent,
    | 'payload'
    | 'metadata'
    | 'sequence_number'
    | 'device_sequence_number'
  > {
  payload: T
  metadata: M
  sequence_number?: number
  device_sequence_number?: number
}

export type NewDomainEvent<
  T = unknown,
  M = EventMetadata
> = Omit<
  TDomainEvent<T, M>,
  | 'id'
  | 'sequence_number'
  | 'device_sequence_number'
  | 'device_id'
  | 'timestamp'
  | 'created_at'
>

export type NewBuilderDomainEvent<Schema> = Pick<
  NewDomainEvent<
    OmitDeleted<OmitCreated<Omit<Schema, 'id'>>> & {
      id?: string
    }
  >,
  'profile_id' | 'user_id' | 'payload'
> &
  Partial<Pick<wasm.DomainEvent, 'correlation_id' | 'metadata'>>

export interface CommittedEvent {
  id: string
  sequence_number: number
  created_at: ISODateTimeString
}

export interface PaginationInfo {
  limit: number
  cursor: number
  next_cursor: number | null
  has_more: boolean
}

/**
 * Response from `GET /v1/events/{aggregate_type}/{profile_id}/status`
 */
export interface RemoteEventsStatus {
  latest_sequence_number: number
  latest_timestamp: ISODateTimeString | null
  last_processed_sequence: number
  projections_updated_at: ISODateTimeString | null
}

export interface PullProgress {
  aggregateType: string
  profileId: string
  /** Highest server sequence number stored locally */
  syncedSequence: number
  /** Latest sequence number on the server, null when unknown */
  remoteSequence: number | null
}

export interface PullResponse {
  pagination: PaginationInfo
  data: TDomainEvent[]
}

export type ProjectionEntityStoreDb = Kysely<
  EventSourcingDatabase &
    Record<
      string,
      SchemaWithId<CreatedFields & DeletedFields & ProfileFields>
    >
>

export interface ProfileProjectionState
  extends EventSourcingDatabase {
  calendar: CalendarSchema
  contact: ContactSchema
  contact_relationship: ContactRelationshipSchema
  credit_note: CreditNoteSchema
  credit_note_line: CreditNoteLineSchema
  wallet_entry: WalletEntrySchema
  invite: InviteSchema
  invoice: InvoiceSchema
  invoice_line: InvoiceLineSchema
  invoice_activity: InvoiceActivitySchema
  location: LocationSchema
  message: MessageSchema
  payment: PaymentSchema
  payment_provider_account: PaymentProviderAccountSchema
  price: PriceSchema
  profile: ProfileSchema
  profile_settings: ProfileSettingsSchema
  profile_subscription: ProfileSubscriptionSchema
  schedule: ScheduleSchema
  schedule_instance: ScheduleInstanceSchema
  subscription_feature: SubscriptionFeatureSchema
  subscription_plan: SubscriptionPlanSchema
  subscription_plan_feature: SubscriptionPlanFeatureSchema
}

export type ProfileProjectionStoreDb =
  Kysely<ProfileProjectionState>

export type ProfileProjectionSelectQueryBuilder<
  T extends keyof ProfileProjectionState
> = SelectQueryBuilder<
  ProfileProjectionState,
  T,
  ProfileProjectionState[T]
>
